import { Button, FormControlLabel, Radio, RadioGroup } from "@mui/material"
import { FC, useEffect, useState } from "react"

import { useAppDispatch, useAppSelector } from "@/core/app/hooks"
import { RootState } from "@/core/app/store"
import {
  EBiteJumpLocationType,
  EShowArchType,
  stageControl,
  stagingManager,
  toothMovement,
  viewControlInTreatment,
  wasmModule,
} from "@/gluelayer"

import { globalMovement } from "@/gluelayer/core/modules/globalMovement"
import IconVec1 from "@/shared/components/Icons/IconVec1"
import IconVec10 from "@/shared/components/Icons/IconVec10"
import IconVec11 from "@/shared/components/Icons/IconVec11"
import IconVec2 from "@/shared/components/Icons/IconVec2"
import IconVec3 from "@/shared/components/Icons/IconVec3"
import IconVec4 from "@/shared/components/Icons/IconVec4"
import IconVec5 from "@/shared/components/Icons/IconVec5"
import IconVec6 from "@/shared/components/Icons/IconVec6"
import IconVec7 from "@/shared/components/Icons/IconVec7"
import IconVec8 from "@/shared/components/Icons/IconVec8"
import IconVec9 from "@/shared/components/Icons/IconVec9"
import MyDialog from "@/shared/components/MyDialog"
import {
  EBiteJump,
  EGMButtonTypes,
  ETopLeftViewType,
  IGMListItem,
  ISelectItem,
} from "@/shared/types"
import utils from "@/shared/utils"
import {
  setIsShowExtractedMsg,
  setStageGmLocation,
  setTopLeftViewType,
} from "@/UDTreat/udTreatSlice"
import GMButtons from "./GMButtons"

interface IProps {
  setPageLoading: (loading: boolean) => void
}

export enum EDirection {
  UP = "up",
  DOWN = "down",
}

const BiteJumpList: ISelectItem[] = [
  {
    label: "Beginning",
    value: EBiteJump.BEGINNING,
  },
  {
    label: "Throughout",
    value: EBiteJump.THROUGHOUT,
  },
  {
    label: "End",
    value: EBiteJump.END,
  },
]

const GMList: IGMListItem[] = [
  {
    type: EGMButtonTypes.GROUP_1,
    iconLeft: <IconVec5 width={40} height={17} />,
    iconRight: <IconVec6 width={40} height={17} />,
    value: "0.00",
    unit: "mm",
  },
  {
    type: EGMButtonTypes.GROUP_2,
    iconLeft: <IconVec10 />,
    iconRight: <IconVec9 />,
    value: "0.00",
    unit: "mm",
  },
  {
    type: EGMButtonTypes.GROUP_3,

    iconLeft: <IconVec2 />,
    iconRight: <IconVec1 />,
    value: "0.00",
    unit: "mm",
  },
  {
    type: EGMButtonTypes.GROUP_4,
    iconLeft: <IconVec3 width={40} height={22} />,
    iconRight: <IconVec4 />,
    value: "0.00",
    unit: "°",
  },
  {
    type: EGMButtonTypes.GROUP_5,
    iconLeft: <IconVec11 />,
    iconRight: <IconVec11 />,
    value: "0.00",
    unit: "°", //Degree
  },
  {
    type: EGMButtonTypes.GROUP_6,
    iconLeft: <IconVec8 />,
    iconRight: <IconVec7 />,
    value: "0.00",
    unit: "°", //Degree
  },
]

const { setStageCallback, setStageStep, getWasmStageData } = stageControl
const GlobalMovement: FC<IProps> = ({ setPageLoading }) => {
  // const [isShowWidget, setIsShowWidget] = useState<boolean>(false)
  const dispatch = useAppDispatch()
  const [radioValue, setRadioValue] = useState<EBiteJump | null>(EBiteJump.END)
  const [list, setList] = useState<IGMListItem[]>(GMList)
  const [isShowClearAllDialog, setIsShowClearAllDialog] =
    useState<boolean>(false)
  const { stageGmLocation, isShowExtractedMsg, updataGM } = useAppSelector(
    (state: RootState) => state.udTreatService,
  )
  /**
   * Set Init Data
   */
  const setInitData = (dList: number[], rList: number[]) => {
    const newList = list.map((item, index) => {
      if (item.type === EGMButtonTypes.GROUP_1) {
        return {
          ...item,
          value: dList[0].toFixed(2),
        }
      } else if (item.type === EGMButtonTypes.GROUP_2) {
        return {
          ...item,
          value: dList[1].toFixed(2),
        }
      } else if (item.type === EGMButtonTypes.GROUP_3) {
        return {
          ...item,
          value: dList[2].toFixed(2),
        }
      } else if (item.type === EGMButtonTypes.GROUP_4) {
        return {
          ...item,
          value: rList[3].toFixed(2),
        }
      } else if (item.type === EGMButtonTypes.GROUP_5) {
        return {
          ...item,
          value: rList[1].toFixed(2),
        }
      } else if (item.type === EGMButtonTypes.GROUP_6) {
        return {
          ...item,
          value: rList[2].toFixed(2),
        }
      }
      return item
    })
    setList(newList)
  }

  const setFinal = () => {
    stagingManager.SetStageToFinal(EShowArchType.BothArch)
    stagingManager.SetGMToFinal()
  }

  /**
   * Init Data
   */
  const initGMData = async () => {
    if (!globalMovement.GetModuleLowerArchMovement()) {
      return
    }

    const distanceList = [0, 0, 0]
    const rotateList = [0, 0, 0, 0]
    const distance = globalMovement.GetMoveDistance()

    for (let i = 0; i < distance.size(); i++) {
      distanceList[i] = distance.get(i)
    }
    const rotate = globalMovement.GetRotateAngle()
    for (let i = 0; i < rotate.size(); i++) {
      rotateList[i] = rotate.get(i)
    }
    // console.log(distanceList, rotateList, "=========init")
    setInitData(distanceList, rotateList)

    stagingManager.SetBiteJumpType(
      stagingManager.ConvertBiteJump(stageGmLocation),
    )

    //set the ui location by stage gm location
    setRadioValue(stageGmLocation)

    stagingManager.gmStatusChanged()
  }

  const initView = async (isInit: boolean) => {
    if (!isInit) {
      dispatch(setIsShowExtractedMsg(false))
      toothMovement.setIsEnableMoveTooth(true)
      wasmModule.moduleManager.SwitchAttachmentModule(true)
      globalMovement.ToggleLowerArchMove(false)
      return
    }
    dispatch(setIsShowExtractedMsg(true))
    dispatch(setTopLeftViewType(ETopLeftViewType.FRONT))
    viewControlInTreatment.setViewType(ETopLeftViewType.FRONT)
    //set gm to final
    setFinal()
    toothMovement.setIsEnableMoveTooth(false)
    wasmModule.moduleManager.SwitchAttachmentModule(false)
    globalMovement.ToggleLowerArchMove(true)
  }

  useEffect(() => {
    initView(true)
    initGMData()
    return () => {
      initView(false)
    }
  }, [])

  useEffect(() => {
    initGMData()
  }, [updataGM])

  /**
   * Updata
   */
  const Updata = async (
    isPlus: boolean,
    direction: number,
    step: number,
    newList: IGMListItem[],
    isUpdate: boolean,
  ) => {
    setFinal()

    if (isUpdate) {
      //set the stage gm location, for stage bar, user may change the bite jump type firstly, then modify
      stagingManager.SetBiteJumpType(
        stagingManager.ConvertBiteJump(stageGmLocation),
      )
      globalMovement.SetControllArchMove(isPlus, direction, step)
      //inform stage update the gm status
      stagingManager.gmStatusChanged()
    }
    setList(newList)
  }

  /**
   * Reset
   */
  const onReset = async () => {
    globalMovement.OnClearAll()
    const newList = list.map((item) => {
      return {
        ...item,
        value: "0.00",
      }
    })
    setList(newList)
    setIsShowClearAllDialog(false)
    await utils.sleep(10)
    setPageLoading(false)
    //set the stage gm location, for stage bar, user may change the bite jump type firstly, then modify
    stagingManager.SetBiteJumpType(
      stagingManager.ConvertBiteJump(stageGmLocation),
    )
    //inform stage update the gm status
    stagingManager.gmStatusChanged()
  }

  /**
   *
   */
  const handleShowBiteJump = (e) => {
    const value = e.target.value as EBiteJump
    const mapValue =
      value === EBiteJump.BEGINNING
        ? EBiteJumpLocationType["EBiteJumpLocation_Start"]
        : value === EBiteJump.THROUGHOUT
        ? EBiteJumpLocationType["EBiteJumpLocation_All"]
        : value === EBiteJump.END
        ? EBiteJumpLocationType["EBiteJumpLocation_End"]
        : EBiteJumpLocationType["EBiteJumpLocation_End"]
    stagingManager.SetBiteJumpType(mapValue)
    setRadioValue(e.target.value as EBiteJump)

    //set the stage gm location, for stage bar
    dispatch(setStageGmLocation(value))
  }

  return (
    <div className="card-item-body ">
      <div className="global-movement-content">
        <div className="global-movement-content-top">
          {/* <div className="global-movement-title">GM Widget</div>
          <div className="d-flex gap-50">
            {isShowWidget ? (
              <Tooltip title="Hide 3D Widget" placement="top">
                <VisibilityIcon
                  className="hover"
                  style={{ color: "#215ecd", fontSize: "20px" }}
                  onClick={() => setIsShowWidget(false)}
                />
              </Tooltip>
            ) : (
              <Tooltip title="Show 3D Widget" placement="top">
                <VisibilityOutlinedIcon
                  className="hover"
                  style={{ color: "#ccc", fontSize: "20px" }}
                  onClick={() => setIsShowWidget(true)}
                />
              </Tooltip>
            )}

            <Tooltip title="Clear All" placement="top">
              <RefreshIcon
                className="hover"
                style={{ fontSize: "20px", color: "#215ecd" }}
              />
            </Tooltip>
          </div> */}

          <Button
            className="btn btn-primary"
            variant="text"
            onClick={() => setIsShowClearAllDialog(true)}
          >
            Clear All
          </Button>
        </div>
        <GMButtons list={list} Updata={Updata} />
      </div>
      <div className="card-item-sub-title">Show Bite Jump:</div>
      <RadioGroup
        className="bite-jump-radio-group"
        value={radioValue}
        name="bite-jump-radio-group"
        onChange={handleShowBiteJump}
      >
        {BiteJumpList.map((item) => (
          <FormControlLabel
            className="item-radio"
            key={item.value}
            value={item.value}
            control={<Radio size="small" />}
            label={item.label}
          />
        ))}
      </RadioGroup>

      {/*Clear ALl Dialog */}
      <MyDialog
        open={isShowClearAllDialog}
        title="Warning"
        content="Clear All will delete all global movements. Proceed?"
        actions={
          <div className="d-flex gap-1">
            <Button
              className="btn btn-primary"
              variant="contained"
              onClick={onReset}
            >
              Yes
            </Button>
            <Button
              className="btn btn-outline-primary"
              variant="outlined"
              onClick={() => setIsShowClearAllDialog(false)}
            >
              No
            </Button>
          </div>
        }
      />
    </div>
  )
}

export default GlobalMovement
